import React from "react";
import { X, RefreshCcw, CheckCircle2, AlertTriangle, XCircle, Circle, Activity } from "lucide-react";

/**
 * SystemHealthDrawer — slide-in panel with full dependency breakdown.
 * Mounted at root of AdminPanelPage. Opens via the CommandHeader badge.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   data: payload from /api/system/health
 *   loading: bool
 *   error: string | null
 *   onRefresh: () => void
 */
const STATUS_META = {
  ok:       { color: "emerald", Icon: CheckCircle2, label: "OK" },
  degraded: { color: "amber",   Icon: AlertTriangle, label: "DÉGRADÉ" },
  down:     { color: "rose",    Icon: XCircle,       label: "PANNE" },
  disabled: { color: "zinc",    Icon: Circle,        label: "DÉSACTIVÉ" },
  unknown:  { color: "zinc",    Icon: Circle,        label: "INCONNU" },
};

const COLOR_CLASSES = {
  emerald: {
    border: "border-emerald-500/40",
    bg:     "bg-emerald-500/10",
    text:   "text-emerald-300",
    dot:    "bg-emerald-400",
  },
  amber: {
    border: "border-amber-500/50",
    bg:     "bg-amber-500/10",
    text:   "text-amber-200",
    dot:    "bg-amber-400",
  },
  rose: {
    border: "border-rose-500/60",
    bg:     "bg-rose-500/15",
    text:   "text-rose-200",
    dot:    "bg-rose-400",
  },
  zinc: {
    border: "border-zinc-700",
    bg:     "bg-zinc-800/40",
    text:   "text-zinc-400",
    dot:    "bg-zinc-500",
  },
};

function DependencyCard({ depKey, dep }) {
  const meta = STATUS_META[dep.status] || STATUS_META.unknown;
  const c = COLOR_CLASSES[meta.color];
  const { Icon } = meta;
  // Optional extras to surface in the body.
  const extras = Object.entries(dep).filter(([k]) =>
    !["status", "label", "detail", "latency_ms"].includes(k)
  );
  return (
    <div
      data-testid={`health-dep-${depKey}`}
      className={`border ${c.border} ${c.bg} p-3 flex flex-col gap-1.5`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 ${c.text} shrink-0`} strokeWidth={2.5} />
          <span className="font-mono text-[11px] font-bold tracking-wider text-zinc-100 truncate">
            {dep.label}
          </span>
        </div>
        <span
          data-testid={`health-dep-${depKey}-status`}
          className={`font-mono text-[9px] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5 border ${c.border} ${c.text}`}
        >
          {meta.label}
        </span>
      </div>
      <div className="text-[11px] text-zinc-300 leading-snug pl-6 break-words">
        {dep.detail || "—"}
      </div>
      {(extras.length > 0 || dep.latency_ms != null) && (
        <div className="pl-6 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono text-zinc-500 tabular-nums">
          {dep.latency_ms != null && (
            <span title="Latence aller-retour">⟳ {dep.latency_ms} ms</span>
          )}
          {extras.map(([k, v]) => (
            <span key={k}>
              <span className="text-zinc-600">{k}=</span>
              <span className="text-zinc-400">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export const SystemHealthDrawer = ({ open, onClose, data, loading, error, onRefresh }) => {
  if (!open) return null;
  const overall = data?.overall || "unknown";
  const meta = STATUS_META[overall] || STATUS_META.unknown;
  const c = COLOR_CLASSES[meta.color];
  const deps = data?.dependencies || {};
  const depKeys = ["db", "redis", "storage", "sms", "email", "push"];
  const checked = data?.checked_at ? new Date(data.checked_at * 1000) : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      data-testid="health-drawer"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="health-drawer-scrim"
      />
      <aside className="relative w-full sm:w-[420px] h-full bg-[#0b0f14] border-l border-zinc-800 shadow-2xl flex flex-col cmd-scanlines">
        {/* Header */}
        <div className={`border-b ${c.border} ${c.bg} px-4 py-3 flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <Activity className={`w-5 h-5 ${c.text} shrink-0`} strokeWidth={2.5} />
            <div className="min-w-0">
              <div className="text-[9px] font-bold tracking-[0.3em] uppercase text-zinc-500">
                Diagnostic temps réel
              </div>
              <div className={`font-mono text-sm font-bold tracking-wider ${c.text}`} data-testid="health-overall">
                {overall === "ok"       && "TOUS SYSTÈMES OPÉRATIONNELS"}
                {overall === "degraded" && "DÉGRADATION DÉTECTÉE"}
                {overall === "down"     && "PANNE EN COURS"}
                {overall === "unknown"  && "SONDE EN COURS…"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="health-drawer-close"
            className="shrink-0 p-1.5 border border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && !data ? (
            <div className="text-[11px] text-zinc-400 font-mono tracking-wider">
              Probe initial en cours…
            </div>
          ) : null}
          {error && (
            <div className="border border-rose-500/60 bg-rose-500/10 text-rose-200 text-[11px] p-2 font-mono">
              Erreur de sonde&nbsp;: {error}
            </div>
          )}
          {depKeys.map((k) =>
            deps[k] ? <DependencyCard key={k} depKey={k} dep={deps[k]} /> : null
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-800 px-4 py-2.5 flex items-center justify-between text-[10px] font-mono text-zinc-500">
          <div data-testid="health-checked-at">
            {checked ? `Dernière sonde · ${checked.toLocaleTimeString("fr-FR")}` : "—"}
          </div>
          <button
            type="button"
            onClick={onRefresh}
            data-testid="health-refresh-btn"
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2 py-1 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 disabled:opacity-50"
          >
            <RefreshCcw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Re-sonder
          </button>
        </div>
      </aside>
    </div>
  );
};

export default SystemHealthDrawer;
